import React, { useState, useEffect, useRef } from 'react';
import { InterventionAreas } from '../../types/policyImpact';
import PolicyLeverControl from './PolicyLeverControl';
import './InterventionAreasControl.css';

interface SubPolicyValues {
  transitOrientedDevelopment: number;
  activeTransportation: number;
  transitImprovements: number;
  parkingPricing: number;
  micromobility: number;
  otherInitiatives: number; // User-adjustable
}

interface InterventionAreasControlProps {
  interventionAreas: InterventionAreas;
  onChange: (updatedAreas: InterventionAreas) => void;
  onExpandedChange?: (expanded: keyof InterventionAreas | null) => void;
  subPolicyValues?: SubPolicyValues;
  onSubPolicyChange?: (subPolicyValues: SubPolicyValues) => void;
  onSubPolicyExpand?: (key: string | null) => void;
  parkingVMTReductionPercent?: number; // Calculated parking pricing VMT reduction %
  computeParkingVMTReductionPercent?: (priceIncreasePercent: number) => number; // Function to calculate parking VMT reduction
  micromobilityVMTReductionPercent?: number; // Calculated micromobility VMT reduction %
  transitVMTReductionPercent?: number; // Calculated transit service VMT reduction %
  vehicleElectrificationMinPercent?: number; // Minimum EV% (current EV share)
  onLoadScenario?: (scenarioName: string) => void; // Handler for loading predefined scenarios
  onResetScenario?: () => void; // Handler for resetting scenario
}

interface InterventionData {
  key: keyof InterventionAreas;
  label: string;
  description: string;
  min: number;
  max: number;
  color: string;
  bgColor: string;
}

const interventions: InterventionData[] = [
  // Transportation - Light Blue
  { key: 'vmtReduction', label: 'VMT Reduction', description: 'Reduce vehicle miles traveled through transit, biking, and other initiatives', min: 0, max: 20, color: '#bfdbfe', bgColor: '#eff6ff' },
  { key: 'vehicleElectrification', label: 'Vehicle Electrification', description: 'Percentage of vehicles that will be electric by 2045', min: 0, max: 100, color: '#bfdbfe', bgColor: '#eff6ff' },
  // Buildings - Yellow
  { key: 'energyEfficiency', label: 'Energy Efficiency', description: 'Improve building energy efficiency through retrofits and standards', min: 0, max: 40, color: '#fef08a', bgColor: '#fefce8' },
  { key: 'buildingsElectrification', label: 'Building Electrification', description: 'Replace fossil fuel heating/cooking with electric alternatives', min: 0, max: 100, color: '#fef08a', bgColor: '#fefce8' },
  { key: 'gridDecarbonization', label: 'Clean Electricity Year', description: 'Year electricity reaches zero emissions (earlier = faster grid decarbonization)', min: 2025, max: 2045, color: '#fef08a', bgColor: '#fefce8' },
  // Other - Orange
  { key: 'organicsDiversion', label: 'Organics Diversion', description: 'Divert organic waste from landfills to reduce methane emissions', min: 50, max: 100, color: '#fed7aa', bgColor: '#fff7ed' },
];

interface VMTSubPolicy {
  key: string;
  label: string;
  value: number;
  min: number;
  max: number;
}

const InterventionAreasControl: React.FC<InterventionAreasControlProps> = ({
  interventionAreas,
  onChange,
  onExpandedChange,
  subPolicyValues = { transitOrientedDevelopment: 0, activeTransportation: 0, transitImprovements: 0, parkingPricing: 0, micromobility: 0, otherInitiatives: 0 },
  onSubPolicyChange,
  onSubPolicyExpand,
  parkingVMTReductionPercent = 0,
  computeParkingVMTReductionPercent,
  micromobilityVMTReductionPercent = 0,
  transitVMTReductionPercent = 0,
  vehicleElectrificationMinPercent,
  onLoadScenario,
  onResetScenario,
}) => {
  // Clean Electricity Year slider UX:
  // - Left = 2045 (higher emissions), Right = 2025 (lower emissions)
  // Internally, we still store the actual year (2025-2045) in interventionAreas.gridDecarbonization.
  const CLEAN_ELEC_YEAR_MIN = 2025;
  const CLEAN_ELEC_YEAR_MAX = 2045;
  const cleanYearToUi = (year: number) => {
    const y = Math.max(CLEAN_ELEC_YEAR_MIN, Math.min(CLEAN_ELEC_YEAR_MAX, Number(year) || CLEAN_ELEC_YEAR_MAX));
    return CLEAN_ELEC_YEAR_MAX - y; // 0..20
  };
  const uiToCleanYear = (uiValue: number) => {
    const v = Math.max(0, Math.min(CLEAN_ELEC_YEAR_MAX - CLEAN_ELEC_YEAR_MIN, Number(uiValue) || 0));
    return CLEAN_ELEC_YEAR_MAX - v;
  };
  const [expandedIntervention, setExpandedIntervention] = useState<keyof InterventionAreas | null>(null);
  const [expandedSubPolicy, setExpandedSubPolicy] = useState<string | null>(null);
  const [scenarioDropdownOpen, setScenarioDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setScenarioDropdownOpen(false);
      }
    };

    if (scenarioDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [scenarioDropdownOpen]);
  
  // Calculate sum of policy reductions (excluding Other Initiatives)
  // Note: transitImprovements in subPolicyValues is the service increase %, not VMT reduction %
  // Use transitVMTReductionPercent (calculated) instead
  const sumOfPolicyReductions = 
    (subPolicyValues.transitOrientedDevelopment || 0) +
    (subPolicyValues.activeTransportation || 0) +
    transitVMTReductionPercent + // Use calculated VMT reduction %, not service increase %
    parkingVMTReductionPercent +
    micromobilityVMTReductionPercent;

  
  // Other Initiatives: when VMT Reduction is expanded, calculate from top slider
  // When collapsed, use the stored value
  const otherInitiativesValue = expandedIntervention === 'vmtReduction'
    ? Math.max(0, interventionAreas.vmtReduction - sumOfPolicyReductions)
    : (subPolicyValues.otherInitiatives || 0);
  
  // VMT Reduction sub-policies
  // Note: For parking pricing and micromobility, the slider controls the policy level (price increase % or scale %),
  // not the resulting VMT reduction percentage. The VMT reduction is calculated separately.
  const vmtSubPolicies = [
    { key: 'transitOrientedDevelopment', label: 'Transit-Oriented Development', min: 0, max: 15, value: subPolicyValues.transitOrientedDevelopment || 0 },
    { key: 'activeTransportation', label: 'Expand active transportation infrastructure by', min: 0, max: 10, value: subPolicyValues.activeTransportation || 0 },
    { key: 'transitImprovements', label: 'Expand transit service by', min: 0, max: 50, value: subPolicyValues.transitImprovements || 0 },
    { key: 'parkingPricing', label: 'Increase parking price by', min: 0, max: 300, value: subPolicyValues.parkingPricing || 0 },
    { key: 'micromobility', label: 'Expand micromobility by', min: 0, max: 200, value: subPolicyValues.micromobility || 0 },
    { key: 'otherInitiatives', label: '% Reduction Other Initiatives', min: 0, max: 50, value: otherInitiativesValue },
  ];

  const handleSubPolicyValueChange = (key: string, value: number) => {
    if (onSubPolicyChange) {
      const updatedValues = {
        ...subPolicyValues,
        [key as keyof SubPolicyValues]: value,
      };
      onSubPolicyChange(updatedValues);
      
      // Other Initiatives is read-only (indicator only), so skip handling changes to it
      if (key === 'otherInitiatives') {
        // Do nothing - Other Initiatives is read-only
        return;
      } else {
        // When other sub-policies change, update total VMT reduction to match the new sum
        // Total = sum of all policy reductions + Other Initiatives
        // This ensures the top-level slider increases when any policy increases
        
        // For parking pricing and micromobility, they are calculated values that will trigger
        // the effect in PolicyImpactTool to update the total. For direct policies (TOD, active transport),
        // we update immediately.
        if (key === 'parkingPricing' || key === 'micromobility') {
          // Don't update total here - let the effect in PolicyImpactTool handle it
          // when the calculated percentages update
          return;
        }
        
        const updatedSumOfPolicyReductions = 
          (key === 'transitOrientedDevelopment' ? value : (subPolicyValues.transitOrientedDevelopment || 0)) +
          (key === 'activeTransportation' ? value : (subPolicyValues.activeTransportation || 0)) +
          transitVMTReductionPercent + // Use calculated VMT reduction %, not service increase %
          parkingVMTReductionPercent +
          micromobilityVMTReductionPercent;
        
        // Keep Other Initiatives constant, update total to match new sum
        const newTotalVmtReduction = updatedSumOfPolicyReductions + otherInitiativesValue;
        onChange({
          ...interventionAreas,
          vmtReduction: Math.max(0, Math.min(20, newTotalVmtReduction)),
        });
      }
    }
  };
  
  const handleExpandClick = (key: keyof InterventionAreas) => {
    const newExpanded = expandedIntervention === key ? null : key;
    setExpandedIntervention(newExpanded);
    if (onExpandedChange) {
      onExpandedChange(newExpanded);
    }
    // Scroll to details section for vehicle electrification
    if (newExpanded === 'vehicleElectrification') {
      setTimeout(() => {
        const element = document.getElementById('vehicle-electrification-details');
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleSubPolicyClick = (key: string) => {
    const newKey = expandedSubPolicy === key ? null : key;
    setExpandedSubPolicy(newKey);
    if (onSubPolicyExpand) onSubPolicyExpand(newKey);
    if (onExpandedChange) onExpandedChange(newKey ? 'vmtReduction' : null);
    if (newKey && (newKey === 'parkingPricing' || newKey === 'micromobility' || newKey === 'transitImprovements')) {
      setTimeout(() => {
        const element = document.getElementById(
          newKey === 'parkingPricing'
            ? 'parking-pricing-details'
            : newKey === 'micromobility'
            ? 'micromobility-details'
            : 'transit-service-details'
        );
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleChange = (key: keyof InterventionAreas, value: number) => {
    let minForKey: number | undefined = undefined;
    
    if (key === 'vehicleElectrification' && typeof vehicleElectrificationMinPercent === 'number') {
      minForKey = vehicleElectrificationMinPercent;
    }
    // Removed VMT Reduction minimum constraint - allow it to go below sum of policy reductions
    
    const clamped = minForKey !== undefined ? Math.max(minForKey, value) : value;
    onChange({
      ...interventionAreas,
      [key]: clamped,
    });
    
    // If VMT Reduction changed, update Other Initiatives to balance
    if (key === 'vmtReduction' && onSubPolicyChange) {
      // Use the same sumOfPolicyReductions calculated above (which uses transitVMTReductionPercent)
      const calculatedOtherInitiatives = Math.max(0, clamped - sumOfPolicyReductions);
      
      onSubPolicyChange({
        ...subPolicyValues,
        otherInitiatives: calculatedOtherInitiatives,
      });
    }
  };

  const handleScenarioSelect = (scenarioName: string) => {
    if (onLoadScenario) {
      onLoadScenario(scenarioName);
    }
    setScenarioDropdownOpen(false);
  };

  const handleResetClick = () => {
    if (onResetScenario) {
      onResetScenario();
    }
    setScenarioDropdownOpen(false);
  };

  return (
    <div className="intervention-areas-control">
      <div className="intervention-areas-header">
        <div>
          <h3 className="intervention-areas-title">Implementation by 2045</h3>
          <p className="intervention-areas-description">
            Adjust each intervention to set the extent of implementation by 2045
          </p>
        </div>
        {(onLoadScenario || onResetScenario) && (
          <div className="scenario-dropdown-container" ref={dropdownRef}>
            <button
              className="scenario-dropdown-button"
              onClick={() => setScenarioDropdownOpen(!scenarioDropdownOpen)}
              aria-expanded={scenarioDropdownOpen}
            >
              Quick Scenarios
              <span className="dropdown-arrow">{scenarioDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {scenarioDropdownOpen && (
              <div className="scenario-dropdown-menu">
                {onLoadScenario && (
                  <>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('baseline')}
                    >
                      Baseline
                    </button>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('fullImplementation')}
                    >
                      Full Implementation
                    </button>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('aggressive')}
                    >
                      Aggressive
                    </button>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('moderate')}
                    >
                      Moderate
                    </button>
                  </>
                )}
                {onResetScenario && (
                  <button
                    className="scenario-dropdown-item scenario-dropdown-item-reset"
                    onClick={handleResetClick}
                  >
                    Reset
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="intervention-table">
        {interventions.map((intervention) => (
          <React.Fragment key={intervention.key}>
            <div 
              className="intervention-table-row"
              style={{ backgroundColor: intervention.bgColor, borderLeftColor: intervention.color }}
            >
            <div className="intervention-table-label-cell">
              <button
                className="intervention-info-button-small"
                onClick={() => handleExpandClick(intervention.key)}
                title={intervention.description}
              >
                {expandedIntervention === intervention.key ? '−' : '+'}
              </button>
              <span className="intervention-item-label-compact">{intervention.label}</span>
            </div>
            <div className="intervention-table-slider-cell">
              <div className="compact-slider-wrapper">
                <input
                  type="range"
                  disabled={intervention.key === 'vmtReduction' && expandedIntervention === 'vmtReduction'}
                  min={intervention.key === 'vehicleElectrification' && typeof vehicleElectrificationMinPercent === 'number'
                    ? vehicleElectrificationMinPercent
                    : intervention.key === 'gridDecarbonization'
                      ? 0
                      : intervention.min}
                  max={intervention.key === 'gridDecarbonization'
                    ? (CLEAN_ELEC_YEAR_MAX - CLEAN_ELEC_YEAR_MIN)
                    : intervention.max}
                  step={
                    intervention.key === 'vehicleElectrification'
                        ? 0.1
                        : intervention.key === 'gridDecarbonization'
                          ? 1
                        : intervention.key === 'vmtReduction'
                          ? 1
                        : 5
                  }
                  value={intervention.key === 'gridDecarbonization'
                    ? cleanYearToUi(Number(interventionAreas.gridDecarbonization))
                    : Number(interventionAreas[intervention.key]) || 0}
                  onChange={(e) => {
                    if (intervention.key === 'gridDecarbonization') {
                      handleChange(intervention.key, uiToCleanYear(Number(e.target.value)));
                    } else {
                      handleChange(intervention.key, Number(e.target.value));
                    }
                  }}
                  className="compact-slider"
                  style={{
                    background: intervention.key === 'gridDecarbonization'
                      ? `linear-gradient(to right, ${intervention.color} 0%, ${intervention.color} ${(cleanYearToUi(Number(interventionAreas.gridDecarbonization)) / (CLEAN_ELEC_YEAR_MAX - CLEAN_ELEC_YEAR_MIN)) * 100}%, #e5e7eb ${(cleanYearToUi(Number(interventionAreas.gridDecarbonization)) / (CLEAN_ELEC_YEAR_MAX - CLEAN_ELEC_YEAR_MIN)) * 100}%, #e5e7eb 100%)`
                      : (() => {
                          const sliderMin = intervention.key === 'vehicleElectrification' && typeof vehicleElectrificationMinPercent === 'number'
                            ? vehicleElectrificationMinPercent
                            : intervention.min;
                          const sliderMax = intervention.max;
                          const currentValue = Number(interventionAreas[intervention.key]) || 0;
                          
                          // Calculate percentage to match knob position: (value - min) / (max - min) * 100
                          const range = sliderMax - sliderMin;
                          if (range <= 0) {
                            return `linear-gradient(to right, ${intervention.color} 0%, ${intervention.color} 0%, #e5e7eb 0%, #e5e7eb 100%)`;
                          }
                          const percentage = ((currentValue - sliderMin) / range) * 100;
                          const clampedPercentage = Math.max(0, Math.min(100, percentage));
                          return `linear-gradient(to right, ${intervention.color} 0%, ${intervention.color} ${clampedPercentage}%, #e5e7eb ${clampedPercentage}%, #e5e7eb 100%)`;
                        })(),
                    '--thumb-border-color': intervention.color === '#bfdbfe' ? '#3b82f6' : intervention.color === '#fef08a' ? '#f59e0b' : '#ea580c',
                  } as React.CSSProperties}
                />
                <div className="compact-slider-value">
                  {intervention.key === 'vehicleElectrification'
                    ? `${Number(interventionAreas[intervention.key]).toFixed(1)}%`
                    : intervention.key === 'gridDecarbonization'
                      ? `${Math.round(Number(interventionAreas.gridDecarbonization))}`
                      : intervention.key === 'vmtReduction'
                        ? `${Number(interventionAreas[intervention.key]).toFixed(1)}%`
                        : `${interventionAreas[intervention.key]}%`}
                </div>
              </div>
            </div>
          </div>

          {/* VMT Reduction Sub-policies */}
          {intervention.key === 'vmtReduction' && expandedIntervention === 'vmtReduction' && (
            <>
              {vmtSubPolicies.map((subPolicy) => (
                <div 
                  key={subPolicy.key}
                  className="intervention-sub-policy-row"
                  style={{ 
                    backgroundColor: '#dbeafe',
                    borderLeftColor: '#3b82f6' 
                  }}
                >
                  <div className="intervention-table-label-cell">
                    <button
                      className="intervention-info-button-small"
                      onClick={() => handleSubPolicyClick(subPolicy.key)}
                      title="Click to expand details"
                    >
                      {expandedSubPolicy === subPolicy.key ? '−' : '+'}
                    </button>
                    <span className="intervention-item-label-compact sub-policy-indent">{subPolicy.label}</span>
                  </div>
                  <div className="intervention-table-slider-cell">
                    <div className="compact-slider-wrapper">
                      {subPolicy.key === 'otherInitiatives' ? (
                        // Other Initiatives: Read-only indicator bar (no knob)
                        <>
                          <div
                            className="compact-slider-indicator-bar"
                            style={{
                              background: `linear-gradient(to right, #bfdbfe 0%, #bfdbfe ${((subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)) / subPolicy.max) * 100}%, #e5e7eb ${((subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)) / subPolicy.max) * 100}%, #e5e7eb 100%)`,
                              pointerEvents: 'none',
                            }}
                          />
                          <div className="compact-slider-value">
                            {(subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)).toFixed(1)}%
                          </div>
                        </>
                      ) : (
                        // Other sub-policies: Interactive slider
                        <>
                          <input
                            type="range"
                            min={subPolicy.min}
                            max={subPolicy.max}
                            step={subPolicy.key === 'parkingPricing' ? 10 : 1}
                            value={subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)}
                            onChange={(e) => {
                              handleSubPolicyValueChange(subPolicy.key, Number(e.target.value));
                            }}
                            className="compact-slider"
                            style={{
                              background: `linear-gradient(to right, #bfdbfe 0%, #bfdbfe ${((subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)) / subPolicy.max) * 100}%, #e5e7eb ${((subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)) / subPolicy.max) * 100}%, #e5e7eb 100%)`,
                              '--thumb-border-color': '#3b82f6',
                            } as React.CSSProperties}
                          />
                          <div className="compact-slider-value">
                            {(subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)).toFixed(1)}%
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default InterventionAreasControl;
